import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { CalendarGrid } from './components/CalendarGrid';
import { CreateEventModal } from './components/CreateEventModal';
import { EventList } from './components/EventList';
import { MonthSelector } from './components/MonthSelector';
import { useCalendarStore } from './stores/useCalendarStore';

import LoadingSpinner from '@/common/components/LoadingSpinner';
import { TabSelector } from '@/common/components/TabSelector';
import { useWidgetConfig } from '@/common/hooks/useWidgetConfig';

type CalendarConfig = {
  activeTab: 'calendar' | 'events';
};

export default function Calendar({ widgetId }: { widgetId: string }) {
  const { t } = useTranslation();
  const TABS = [
    { id: 'calendar' as const, label: t('calendarApp.tabCalendar') },
    { id: 'events' as const, label: t('calendarApp.tabUpcoming') },
  ];
  const { events, loading, fetchEvents, addEvent, deleteEvent } = useCalendarStore();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { config, updateConfig } = useWidgetConfig<CalendarConfig>(widgetId, {
    activeTab: 'calendar',
  });

  useEffect(() => {
    fetchEvents(widgetId);
  }, [fetchEvents, widgetId]);

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    setIsCreateModalOpen(true);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 overflow-auto">
        <TabSelector
          activeTab={config.activeTab}
          className="mx-4 mb-4"
          tabs={TABS}
          onTabChange={(tab) => updateConfig({ activeTab: tab })}
        />

        {config.activeTab === 'calendar' ? (
          <div className="flex gap-4 h-full">
            <div className="w-full bg-white rounded-xl shadow-sm flex flex-col">
              <MonthSelector
                currentDate={currentDate}
                onMonthChange={setCurrentDate}
              />
              <div className="flex-1 min-h-0">
                <CalendarGrid
                  currentDate={currentDate}
                  events={events}
                  onDateClick={handleDateClick}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm">
            <EventList
              events={events}
              onDeleteEvent={(id) => deleteEvent(id)}
            />
          </div>
        )}
      </div>

      {loading && <LoadingSpinner />}

      <CreateEventModal
        isOpen={isCreateModalOpen}
        selectedDate={selectedDate}
        onClose={() => {
          setIsCreateModalOpen(false);
          setSelectedDate(null);
        }}
        onSubmit={async (eventData) => {
          await addEvent(widgetId, eventData);
          setIsCreateModalOpen(false);
          setSelectedDate(null);
        }}
      />
    </div>
  );
}
